import React, { useState } from 'react';
import { Button, Form, Modal, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Select } from '../../../components';
import { useEffect } from 'react';
import useComplexBilling from '../../../invoices/useComplexBilling';

const ComplexCpmInput = ({composite, onChange=f=>f}) => {
  const defaultValue = [{ from: 0, to: "", model: "cpm", value: null }]
  const valueToComplex = (val)=> {
    if( !val ) { return defaultValue }
    const sortedKeys = Object.keys(val).sort((a,b)=>(parseInt(a) - parseInt(b)))
    return sortedKeys.map((k, i) => {
      return {
        from: parseInt(k),
        to: sortedKeys.at(i+1) ? parseInt(sortedKeys.at(i+1)) : "",
        model: Object.keys(val[k])[0],
        value: parseFloat(Object.values(val[k])[0])
      }
    })
  }
  const complexTovalue = (comp)=> {
    const res = {}
    comp.forEach((row) => {
      res[row.from.toString()] = {
        [row.model]: row.value
      }
    })
    return res
  }
  const { t } = useTranslation();
  const { complex, setComplex, updateFrom, updateTo, updateValue, update, add, pop } = useComplexBilling(valueToComplex(composite), [{name: "model", defaultValue: "cpm"}])
  const isToInvald = (i) => {
    return !( complex[i].to === "" || complex[i].from <= complex[i].to)
  }
  const updateModel = (i) => {
    return update(i)("model")
  }
  const isValueInvalid = (i) => {
    return (isNaN(complex[i].value) || complex[i].value < 0 )
  }
  const disabledAdd = () => {
    for (let i = 0; i < complex.length; i++) {
      if(isValueInvalid(i)){ return true }
      if(isToInvald(i)){ return true }
    }
    if ( ! complex.at(-1).to ) { return true }
    return false
  }
  const disabledRemove = () => {
    return (complex.length <= 1)
  }

  useEffect(() => {
    onChange(complexTovalue(complex))
  }, [complex])

  return (
    <>
      <Row>
        <Col>
          <Form.Label>{t('from')}</Form.Label>
        </Col>
        <Col>
          <Form.Label>{t('to')}</Form.Label>
        </Col>
        <Col>
          <Form.Label>{t('billing.model')}</Form.Label>
        </Col>
        <Col>
          <Form.Label>{t('cpm')}</Form.Label>
        </Col>
      </Row>
      { complex.map((elt, i) => {
        return (
          <Row key={i}>
            <Form.Group as={Col} controlId={`from${i}`}>
              <Form.Control
                required
                type="number"
                step="1"
                disabled
                value={elt.from}
                onChange={(e) => {updateFrom(i)(e.target.value)}}
              />
            </Form.Group>
            <Form.Group as={Col} controlId={`to${i}`}>
              <Form.Control
                type="number"
                step="1"
                disabled={false}
                value={elt.to}
                isInvalid={isToInvald(i)}
                onChange={(e) => {
                  updateTo(i)(e.target.value)
                }}
              />
              <Form.Control.Feedback tooltip={true} type="invalid">
                {t("publisher.cpmToValidation")}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} controlId={`model${i}`}>
              <Select
                isClearable={false}
                value={elt.model}
                onChange={(e)=>{updateModel(i)(e)}}
              >
                <Option value="fixed" key="fixed">{t("billing.flat")}</Option>
                <Option value="cpm" key="cpm">{t("billing.cpm")}</Option>
              </Select>
            </Form.Group>
            <Form.Group as={Col} controlId={`value${i}`}>
              <Form.Control
                required
                type="number"
                step="1"
                value={elt.value}
                disabled={false}
                isInvalid={isValueInvalid(i)}
                onChange={(e) => {updateValue(i)(e.target.value)}}
              />
              <Form.Control.Feedback tooltip={true} type="invalid">
                {t("publisher.cpmValueInvalid")}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>
        )
      })}
      <div className="mb-3">
        <Button onClick={add} disabled={disabledAdd()}>add</Button>
        <Button onClick={pop} disabled={disabledRemove()}>remove</Button>
      </div>
    </>
   );
}

export const BillingInput = ({value = {}, onChange=f=>f}) => {
  const { t } = useTranslation();
  const modelFromValue = (value) => {
    if ( !value ) { return null }
    return ["cpm", "fixed", "composite"].find((x) => (x in value))
  }
  const fixValueFromValue = (value) => {
    if(["cpm", "fixed"].includes(modelFromValue(value))) {
      return parseFloat(Object.values(value)[0])
    }
    else { return null }
  }
  const compositeFromValue = (value) => {
    if("composite" == modelFromValue(value)) {
      return value["composite"]
    } else {
      return null
    }
  }
  const [model, setModel] = useState(modelFromValue(value));
  const [fix, setFix] = useState(fixValueFromValue(value));
  const [composite, setComposite] = useState(compositeFromValue(value));
  const [pricing, setPricing] = useState(value)
  const handleSelectChange = (model)=> {
    setModel(model)
  }
  const isFixeValue = () => (["cpm", "fixed"].includes(model))
  const isCompositeValue = () => ("composite" == model)
  useEffect(() => {
    if (isFixeValue()) {
      setPricing({[model]: parseFloat(fix)})
    } else if (isCompositeValue()) {
      setPricing({composite: composite})
    } else {
      setPricing({})
    }
  }, [model, fix, composite])

  useEffect(() => {
    onChange(pricing)
  }, [pricing])

  return (
    <>
      <Form.Group>
        <Form.Label>{t("billing.model")}</Form.Label>
        <Row>
          <Col sm={6}>
            <Select isClearable={true} value={model} onChange={handleSelectChange}>
              <Option value="fixed" key="fixed">{t("billing.flat")}</Option>
              <Option value="cpm" key="cpm">{t("billing.cpm")}</Option>
              <Option value="composite" key="composite">{t("billing.cpm_range")}</Option>
            </Select>
          </Col>
          { isFixeValue() &&
            <Col sm={6}>
            <Form.Control type="number" value={fix} onChange={(e)=>{setFix(e.target.value)}} placeholder="Value" />
          </Col>}
        </Row>
      </Form.Group>
      { isCompositeValue() &&
        < ComplexCpmInput
          composite={value["composite"]}
          onChange={(e)=>(setComposite(e))}
        /> }
    </>
   );
}
